import { DateTime } from "luxon";
import {
  AvailabilityRule,
  DateOverride,
  BlockedDay,
  ShowingStatus
} from "api/scheduling/types";
import {
  GOOGLE_CALENDAR_ICON_SRC,
  MS_CALENDAR_ICON_SRC
} from "shared/constants/calendar-providers";
import { colors, showingStatusConfig } from "./constants";

export interface AvailabilityInterval {
  start: number; // ms timestamp
  end: number;
}

/**
 * Returns the Sunday at the start of the Sun→Sat week that contains `date`.
 *
 * Why this exists: Luxon uses ISO weeks (Mon→Sun), so `DateTime#startOf("week")`
 * returns a Monday. The naive trick of subtracting one day to convert that to a
 * Sunday works for Mon→Sat but breaks on Sunday itself — Sunday is the *end* of
 * Luxon's previous ISO week, so the result lands on the Sunday a full week earlier.
 *
 * `weekday % 7` maps Sun(7)→0, Mon(1)→1, …, Sat(6)→6 — i.e. days since the most
 * recent Sunday — which holds uniformly for every weekday including Sunday.
 *
 * Used wherever we anchor a Sun→Sat calendar week (API range, availability grid).
 */
export const startOfSunSatWeek = (date: DateTime): DateTime =>
  date.minus({ days: date.weekday % 7 }).startOf("day");

// Convert a Luxon DateTime to a JS Date that preserves the wall-clock values
// in the browser's local timezone. This is needed because react-big-calendar
// renders JS Dates using the browser's local timezone.
export const toFakeLocalDate = (dt: DateTime): Date =>
  new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second);

// Split a multi-day timed event into per-day segments so react-big-calendar
// renders them as timed blocks in each day column (like Google Calendar)
// instead of pushing them into the all-day section.
export const splitMultiDayEvent = (
  start: DateTime,
  end: DateTime
): { start: DateTime; end: DateTime }[] => {
  if (!start.isValid || !end.isValid || end <= start) return [{ start, end }];

  // If end is exactly midnight, it visually belongs to the previous day.
  // Clamp to 23:59:59 so react-big-calendar sees start/end on the same day
  // instead of pushing it into the all-day section.
  const clampedEnd =
    end.hour === 0 && end.minute === 0 && end.second === 0
      ? end.minus({ seconds: 1 })
      : end;

  if (start.hasSame(clampedEnd, "day")) return [{ start, end: clampedEnd }];

  const segments: { start: DateTime; end: DateTime }[] = [];
  const endDayKey = clampedEnd.toFormat("yyyy-MM-dd");
  let cursor = start;

  while (cursor.toFormat("yyyy-MM-dd") !== endDayKey) {
    segments.push({ start: cursor, end: cursor.endOf("day") });
    cursor = cursor.plus({ days: 1 }).startOf("day");
    if (segments.length > 366) return [{ start, end: clampedEnd }];
  }

  segments.push({ start: cursor, end: clampedEnd });
  return segments;
};

const intervalFromTimes = (
  dayDate: DateTime,
  startTime: string,
  endTime: string
): AvailabilityInterval => {
  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);
  return {
    start: dayDate
      .set({ hour: startH, minute: startM, second: 0, millisecond: 0 })
      .toMillis(),
    end: dayDate
      .set({ hour: endH, minute: endM, second: 0, millisecond: 0 })
      .toMillis()
  };
};

export function buildAvailabilityIntervals(
  weeklyAvailability: AvailabilityRule[],
  dateOverrides: DateOverride[],
  blockedDays: BlockedDay[],
  weekSunday: DateTime
): AvailabilityInterval[] {
  // day_of_week from API: 0=Sun..6=Sat. weekSunday already starts the week.
  const dayDates = Array.from({ length: 7 }, (_, apiDay) =>
    weekSunday.plus({ days: apiDay })
  );

  const blockedDateSet = new Set(blockedDays.map((b) => b.blocked_date));
  const overrideDateSet = new Set(dateOverrides.map((o) => o.override_date));

  const recurringIntervals = weeklyAvailability.flatMap((rule) => {
    const dayDate = dayDates[rule.day_of_week];
    const dateStr = dayDate.toFormat("yyyy-MM-dd");
    if (blockedDateSet.has(dateStr) || overrideDateSet.has(dateStr)) return [];
    return [intervalFromTimes(dayDate, rule.start_time, rule.end_time)];
  });

  const overrideIntervals = dateOverrides.flatMap((override) => {
    if (override.unavailable_all_day) return [];
    if (!override.start_time || !override.end_time) return [];
    return [
      intervalFromTimes(
        DateTime.fromISO(override.override_date),
        override.start_time,
        override.end_time
      )
    ];
  });

  return [...recurringIntervals, ...overrideIntervals];
}

interface ExternalProviderStyle {
  iconSrc: string;
  alt: string;
  label: string;
  eventBg: string;
  textClass: string;
  barClass: string;
}

const EXTERNAL_PROVIDER_CONFIG: Record<string, ExternalProviderStyle> = {
  google: {
    iconSrc: GOOGLE_CALENDAR_ICON_SRC,
    alt: "Google Calendar",
    label: "Google calendar event",
    eventBg: "var(--info-50, #e6f8fe)",
    textClass: "text-info-700",
    barClass: "bg-info-700"
  },
  microsoft: {
    iconSrc: MS_CALENDAR_ICON_SRC,
    alt: "Microsoft Calendar",
    label: "Microsoft calendar event",
    eventBg: "#effbeb",
    textClass: "text-success-800",
    barClass: "bg-success-800"
  }
};

export const getExternalProviderConfig = (
  provider: string
): ExternalProviderStyle =>
  EXTERNAL_PROVIDER_CONFIG[provider] ?? EXTERNAL_PROVIDER_CONFIG.google;

export type PreviewEventStyleKey =
  | "buffer"
  | "showing"
  | "google"
  | "microsoft"
  | "other";

interface PreviewEventStyle {
  bg: string;
  border: string;
  color?: string;
}

export const PREVIEW_EVENT_STYLES: Record<
  PreviewEventStyleKey,
  PreviewEventStyle
> = {
  buffer: { bg: colors.alpha40, border: "none" },
  showing: {
    bg: showingStatusConfig[ShowingStatus.Confirmed].eventBg,
    border: "var(--primary-500, #e6007e)"
  },
  google: {
    bg: EXTERNAL_PROVIDER_CONFIG.google.eventBg,
    border: "var(--info-700, #0082ad)",
    color: "var(--info-700, #0082ad)"
  },
  microsoft: {
    bg: EXTERNAL_PROVIDER_CONFIG.microsoft.eventBg,
    border: "var(--success-800, #327720)",
    color: "var(--success-800, #327720)"
  },
  other: {
    bg: EXTERNAL_PROVIDER_CONFIG.google.eventBg,
    border: "var(--info-700, #0082ad)",
    color: "var(--info-700, #0082ad)"
  }
};
